import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmploiItem, EmploiService } from '../../services/emploi.service';
import { Subject, takeUntil, finalize } from 'rxjs';
import { ChildrenSelectorComponent, Student } from '../../shared/children-selector/children-selector.component';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { LoaderComponent } from "../../shared/loader/loader.component";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface ScheduleItem {
  subject: string;
  teacher: string;
  classroom: string;
  color: string;
  startTime: number;
  endTime: number;
  duration: number; // in minutes
  rowSpan: number;  // number of hour-rows occupied
  batch?: { batchname: string };
}

@Component({
  selector: 'app-emploi',
  standalone: true,
  imports: [CommonModule, ChildrenSelectorComponent, PageTitleComponent, TranslatePipe, LoaderComponent],
  templateUrl: './emploi.component.html',
  styleUrls: ['./emploi.component.css']
})
export class EmploiComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  selectedChild: Student | null = null;
  loading = false;
  error: string | null = null;
  translate: TranslateService; // Declare translate as a class property
  batchName: string | null = null;

  // Generate 1-hour time slots from 08:00 to 16:00
  timeSlots = this.generateTimeSlots();

  // Legend data (computed after data load)
  legendSubjects: { name: string; color: string }[] = [];
  
  private generateTimeSlots(): string[] {
    // 1-hour slots from 08:00 to 18:00 (rows labeled as ranges, e.g., 08:00 - 09:00)
    const slots: string[] = [];
    for (let hour = 8; hour < 18; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return slots;
  }

  weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  scheduleData: { [key: string]: { [key: string]: ScheduleItem | null } } = {};
  occupiedSlots: { [key: string]: Set<string> } = {}; // Track occupied time slots per day

  constructor(
    private emploiService: EmploiService,
    private cd: ChangeDetectorRef,
    private translateService: TranslateService
  ) {
    this.translate = translateService;
  }

  @ViewChild('calendar') calendarElement!: ElementRef;

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onStudentSelected(student: Student): void {
    this.selectedChild = student;
    this.loadScheduleData();
    this.cd.detectChanges();
  }

  // highlight full hour rows
  isHourMark(time: string): boolean {
    return time.endsWith(':00');
  }

  // Show a clear time range label for each row (e.g., 08:00 - 09:00)
  getTimeRangeLabel(time: string): string {
    const start = this.timeStringToMinutes(time);
    const end = start + 60;
    return `${this.toHHMM(start)} - ${this.toHHMM(end)}`;
  }

private loadScheduleData(): void {
  if (!this.selectedChild) return;

  this.loading = true;
  this.error = null;

  const batchId = this.selectedChild.batchId;
  const idStudent = this.selectedChild.idStudent;

  if (!batchId || !idStudent) {
    console.warn('[Emploi] Batch ou Student non défini');
    this.loading = false;
    return;
  }

  console.groupCollapsed('[Emploi] Loading weekly schedule');

  this.emploiService.getWeeklySchedule(batchId, idStudent)
    .pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.loading = false;
        console.groupEnd();
      })
    )
    .subscribe({
      next: (weeklyData: EmploiItem[][]) => {
        // Transformer les données brutes en format horaire
this.scheduleData = this.transformToTimetableFormat(weeklyData);

        // Extraire le nom de la classe à partir du premier item disponible
        const firstItem = weeklyData?.find(day => day.length > 0)?.[0];
        this.batchName = firstItem?.batch?.batchname ?? null;

        // Construire la légende des matières
        const subjectSet = new Map<string, string>();
        for (const day of this.weekDays) {
          const dayMap = this.scheduleData[day];
          if (!dayMap) continue;
          for (const slot of Object.keys(dayMap)) {
            const item = dayMap[slot];
            if (item) subjectSet.set(item.subject, item.color);
          }
        }
        this.legendSubjects = Array.from(subjectSet.entries()).map(([name, color]) => ({ name, color }));

        // Affichage console pour debug
        console.table(this.weekDays.map(d => ({
          day: d,
          items: Object.values(this.scheduleData[d] || {}).filter(Boolean).length
        })));

        this.cd.detectChanges();
      },
      error: (err) => {
        console.error('[COMPONENT] Failed to load schedule', err);
        this.error = 'Impossible de charger l’emploi du temps.';
        this.scheduleData = {};
        this.batchName = null;
        this.legendSubjects = [];
      }
    });
}


  private transformToTimetableFormat(emploiData: any[][]): { [key: string]: { [key: string]: ScheduleItem | null } } {
    const schedule: { [key: string]: { [key: string]: ScheduleItem | null } } = {};
    const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Initialize schedule and occupied slots
    daysOfWeek.forEach(day => {
      schedule[day] = {};
      this.occupiedSlots[day] = new Set();
      this.timeSlots.forEach(slot => {
        schedule[day][slot] = null;
      });
    });

    const toHHMM = (minutes: number) => {
      const h = Math.floor(minutes / 60).toString().padStart(2, '0');
      const m = (minutes % 60).toString().padStart(2, '0');
      return `${h}:${m}`;
    };

    emploiData.forEach((dayArray, index) => {
      const day = daysOfWeek[index];
      if (!dayArray || !day) return;

      let placed = 0;
      dayArray.forEach(item => {
        const duration = item.endTime - item.startTime; // in minutes
        const rowSpan = Math.max(1, Math.ceil(duration / 60)); // number of 60-minute slots
        
        const scheduleItem: ScheduleItem = {
          subject: item.subject,
          teacher: item.teacher,
          classroom: item.classroom,
          color: this.getSubjectColor(item.subject),
          startTime: item.startTime,
          endTime: item.endTime,
          duration: duration,
          rowSpan: rowSpan,
          batch: item.batch // Assign the batch property
        };

        // Align the start to the hour slot the session belongs to (floor to hour)
        const alignedTimeSlot = this.findClosestTimeSlot(item.startTime);
        schedule[day][alignedTimeSlot] = scheduleItem;
        placed++;
        
        // Mark all occupied hour slots
        let currentTime = Math.floor(item.startTime / 60) * 60; // floor to hour
        while (currentTime < item.endTime) {
          const currentTimeStr = toHHMM(currentTime);
          if (this.timeSlots.includes(currentTimeStr)) {
            this.occupiedSlots[day].add(currentTimeStr);
          }
          currentTime += 60;
        }
      });
    });

    return schedule;
  }

  // Convert time string to minutes for easier comparison
  timeStringToMinutes(timeStr: string): number {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }

  // Align a start time to the corresponding hour slot (floor to hour)
  private findClosestTimeSlot(startTime: number): string {
    const hourAligned = Math.floor(startTime / 60) * 60;
    return this.toHHMM(hourAligned);
  }

  toHHMM(minutes: number): string {
    const h = Math.floor(minutes / 60).toString().padStart(2, '0');
    const m = (minutes % 60).toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  getScheduleForTimeAndDay(time: string, day: string): ScheduleItem | null {
    return this.scheduleData[day]?.[time] || null;
  }

  isSlotOccupied(time: string, day: string): boolean {
    return this.occupiedSlots[day]?.has(time) || false;
  }

  shouldShowCell(time: string, day: string): boolean {
    const item = this.getScheduleForTimeAndDay(time, day);
    if (item) {
      // Only show if this is the aligned starting hour slot of the session
      const alignedStart = this.findClosestTimeSlot(item.startTime);
      return time === alignedStart;
    }
    // Show empty cell if slot is not occupied
    return !this.isSlotOccupied(time, day);
  }

  // Palette fallback for unknown subjects
  private palette: string[] = [
    '#EEFDDC', '#c7ddf9', '#FFEFD1', '#d7ffa7', '#EEF5FE'
  ];
  private colorFromPalette(key: string): string {
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = ((hash << 5) - hash) + key.charCodeAt(i);
    const idx = Math.abs(hash) % this.palette.length;
    return this.palette[idx];
  }



  // Get color for subject (idk type subject name from backend)
  private getSubjectColor(subject: string): string {
    const s = (subject).trim().toLowerCase();
    // Use a predefined palette of colors and apply them randomly
    return this.colorFromPalette(s);
  }

  downloadPdf() {
    if (!this.selectedChild || !this.calendarElement) {
      console.warn('Cannot generate PDF: selectedChild or calendarElement is missing.');
      return;
    }

    html2canvas(this.calendarElement.nativeElement).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 295; // A4 height in mm

        // Add a background rectangle for the header
        pdf.setFillColor(240, 240, 240); // Light grey background
        pdf.rect(0, 0, imgWidth, 70, 'F'); // x, y, width, height, style (F for fill)

        // Add title
        pdf.setFontSize(28);
        pdf.setTextColor(0, 0, 0); // Black text color
        pdf.text('Emploi du temps', 105, 30, { align: 'center' });

        // Add child's name and class name on the same line
        pdf.setFontSize(16);
        pdf.setTextColor(0, 0, 0); // Black text color
        const childAndClassText = ` ${this.selectedChild?.nameStudent}                  Classe: ${this.batchName || 'N/A'}`;
        pdf.text(childAndClassText, 105, 55, { align: 'center' });

        // Add a horizontal line for separation
        pdf.setLineWidth(0.5);
        pdf.line(20, 65, 190, 65); // Draw a line from x1, y1 to x2, y2

        const imgHeight = canvas.height * imgWidth / canvas.width;
        let heightLeft = imgHeight;
        let position = 75; // Start calendar image further down after the line

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= (pageHeight - position);

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
        pdf.save('emploi-du-temps.pdf');
      });
    }
  }

